import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './material-color-tool.component.module.scss';
import { genStyles } from '../converter-layout/generator-layout';

const TONES = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 95, 99, 100];

interface State {
    sourceColor: string;
    copied: 'primary' | 'secondary' | 'tertiary' | 'neutral' | null;
}

function hexToHsl(hex: string): [number, number, number] {
    const m = hex.replace('#', '').match(/^([0-9a-f]{6}|[0-9a-f]{3})$/i);
    if (!m) return [0, 0, 50];
    let h = m[0];
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    const r = parseInt(h.substring(0, 2), 16) / 255;
    const g = parseInt(h.substring(2, 4), 16) / 255;
    const b = parseInt(h.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let hue = 0; const lightness = (max + min) / 2;
    let saturation = 0;
    if (max !== min) {
        const d = max - min;
        saturation = lightness > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: hue = ((g - b) / d + (g < b ? 6 : 0)) * 60; break;
            case g: hue = ((b - r) / d + 2) * 60; break;
            case b: hue = ((r - g) / d + 4) * 60; break;
        }
    }
    return [hue, saturation * 100, lightness * 100];
}

function hslToHex(h: number, s: number, l: number): string {
    s = Math.max(0, Math.min(100, s)) / 100;
    l = Math.max(0, Math.min(100, l)) / 100;
    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    const toHex = (x: number) => Math.round(x * 255).toString(16).padStart(2, '0');
    return '#' + toHex(f(0)) + toHex(f(8)) + toHex(f(4));
}

interface Role {
    name: 'primary' | 'secondary' | 'tertiary' | 'neutral';
    hue: number;
    sat: number;
}

export default class MaterialColorToolComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = { sourceColor: '#6366f1', copied: null };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private get roles(): Role[] {
        const [h, s] = hexToHsl(this.state.sourceColor);
        return [
            { name: 'primary', hue: h, sat: s },
            { name: 'secondary', hue: h, sat: Math.max(15, s * 0.4) },
            { name: 'tertiary', hue: (h + 60) % 360, sat: Math.max(20, s * 0.6) },
            { name: 'neutral', hue: h, sat: Math.max(4, s * 0.1) },
        ];
    }

    private toneToL(tone: number): number {
        // Material M3 uses HCT where Tone is perceptual lightness; HSL L is
        // gamma-encoded so mapping identity gives saturated mid-tones and
        // too-dark low tones. Apply an S-shaped curve that pulls low tones
        // darker and high tones lighter while preserving the 0/100 anchors.
        // Not a full HCT replacement (which needs ~50KB of color math) but
        // a closer approximation than identity.
        const t = Math.min(100, Math.max(0, tone)) / 100;
        return (t * t * (3 - 2 * t)) * 100; // smoothstep
    }

    private tonalPalette(role: Role): { tone: number; hex: string }[] {
        return TONES.map(t => ({ tone: t, hex: hslToHex(role.hue, role.sat, this.toneToL(t)) }));
    }

    copy = (role: 'primary' | 'secondary' | 'tertiary' | 'neutral') => {
        const r = this.roles.find(x => x.name === role)!;
        const tones = this.tonalPalette(r);
        const css = `/* ${role} tonal palette */\n${tones.map(t => `--md-${role}-${t.tone}: ${t.hex};`).join('\n')}`;
        navigator.clipboard.writeText(css).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: role });
            this.copyTimeout = setTimeout(() => this.setState({ copied: null }), 2000);
        });
    };

    render() {
        const { sourceColor, copied } = this.state;
        return (
            <div className="row">
                <div className="col-12 col-md-4">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>
                            Source color
                            <div className={styles.colorRow}>
                                <input type="color" value={sourceColor}
                                    onChange={e => this.setState({ sourceColor: e.target.value })} />
                                <input className={styles.input} type="text" value={sourceColor}
                                    onChange={e => this.setState({ sourceColor: e.target.value })} />
                            </div>
                        </label>
                        <p className={styles.note}>
                            Approximates Material 3 tonal palettes for the four key roles. Tones 0 (black) → 100 (white)
                            map via a smoothstep curve to HSL lightness — a rough perceptual fit that intentionally
                            avoids the ~50&nbsp;KB <code>material-color-utilities</code> HCT library. For pixel-perfect
                            M3, generate via that package server-side.
                        </p>
                    </div>
                </div>
                <div className="col-12 col-md-8">
                    <div className={genStyles.section}>
                        {this.roles.map(role => (
                            <div key={role.name} className={styles.roleBlock}>
                                <div className={styles.roleHeader}>
                                    <span className={styles.roleName}>{role.name}</span>
                                    <button className={`${sharedStyles.copyButton} ${copied === role.name ? sharedStyles.copyButtonSuccess : ''}`}
                                        onClick={() => this.copy(role.name)} style={{ padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}>
                                        {copied === role.name ? '✓ Copied' : 'Copy'}
                                    </button>
                                </div>
                                <div className={styles.toneRow}>
                                    {this.tonalPalette(role).map(t => (
                                        <button key={t.tone} type="button" className={styles.toneSwatch} style={{
                                            background: t.hex,
                                            color: t.tone < 50 ? 'white' : 'black',
                                            border: 'none', padding: 0, cursor: 'pointer', font: 'inherit',
                                        }}
                                            title={`Click to copy ${t.hex}`}
                                            onClick={() => navigator.clipboard?.writeText(t.hex)}>
                                            <span className={styles.toneNum}>{t.tone}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }
}
